// UI/UX Design service page with research-focused copy
import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const uiuxService = {
  name: "UI/UX Design",
  promise: "User experiences that feel intuitive and drive engagement",
  primaryKPI: "+67% user retention improvement",
  signals: {
    bestFor: "Products that need better user engagement and satisfaction",
    timeline: "3-5 weeks",
    team: "Senior UX researcher + UI designer",
    deliverables: "Research insights + high-fidelity designs + prototype"
  },
  outputs: [
    "User research and persona development",
    "Information architecture and user flows",
    "High-fidelity wireframes and mockups",
    "Interactive prototype (Figma/Principle)",
    "Design system with components",
    "Usability testing insights",
    "Handoff documentation for developers"
  ],
  process: [
    {
      step: "Research & Discovery",
      description: "Understand your users through interviews and data analysis",
      ownership: "We lead research, you provide user access",
      artifacts: ["User personas", "Journey maps", "Competitive analysis"]
    },
    {
      step: "Information Architecture",
      description: "Structure content and features for optimal user flow",
      ownership: "We design structure, you validate business logic",
      artifacts: ["Site map", "User flows", "Content strategy"]
    },
    {
      step: "Visual Design",
      description: "Create high-fidelity designs that align with your brand",
      ownership: "We design, you approve visual direction",
      artifacts: ["Wireframes", "High-fidelity mockups", "Design system"]
    },
    {
      step: "Prototype & Test",
      description: "Build interactive prototype and validate with real users",
      ownership: "We build and test, you review insights",
      artifacts: ["Interactive prototype", "Usability test results", "Iteration plan"]
    }
  ],
  proof: [
    {
      client: "B2B SaaS platform",
      challenge: "Users were dropping off during onboarding",
      result: "Redesigned onboarding flow based on user research",
      metric: "+73% completion rate in first month"
    },
    {
      client: "Ecommerce marketplace",
      challenge: "Complex checkout process causing cart abandonment",
      result: "Simplified 3-step checkout with progress indicators",
      metric: "+45% conversion rate improvement"
    }
  ],
  faqs: [
    {
      question: "Do you conduct user research?",
      answer: "Yes, we start every project with user interviews, surveys, or analytics analysis to understand real user needs and pain points."
    },
    {
      question: "What's the difference between UI and UX?",
      answer: "UX focuses on user research and experience flow, while UI focuses on visual design. We handle both for a complete solution."
    },
    {
      question: "Can you work with our existing brand?",
      answer: "Absolutely. We'll adapt your brand guidelines to create designs that feel consistent with your company identity."
    },
    {
      question: "Do you provide developer handoff?",
      answer: "Yes, we create detailed specs, assets, and documentation to ensure smooth implementation by your development team."
    },
    {
      question: "How do you measure design success?",
      answer: "We track metrics like task completion rate, user satisfaction scores, conversion rates, and time-to-complete key actions."
    },
    {
      question: "What if users don't like the new design?",
      answer: "We conduct usability testing during the design phase to catch issues early. Post-launch, we offer 2 weeks of revisions."
    }
  ]
};

const UIUXDesign = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="UI UX Design Services - User-Centered Design Agency | AffinityAim"
        description="Expert UI/UX design services improving retention by 67%. User research, prototyping, and conversion-focused interfaces. See our design portfolio."
        canonical="https://www.affinityaim.com/services/ui-ux-design"
        keywords="UI UX design services, user experience design, interface design agency"
        type="service"
      />
      <Navigation />
      <ServiceDetailPage service={uiuxService} />
      <Footer />
    </div>
  );
};

export default UIUXDesign;